import React, {useCallback, useEffect, useRef, useState} from "react";
import {ColumnFilterProps} from "./types";
import {CategorySelector} from "@/components/incident-categories/CategorySelector";
import {TagSelector} from "@/components/incident-tags/TagSelector";
import {ButtonV2} from "@/components/ui/ButtonsV2/ButtonV2";
import {useDropdownPosition} from "@/hooks/useDropdownPosition";

type Tag = [string, string]; // [slug, label]

type FilterMode = "types" | "tags";

export const IncidentTypeTagFilter: React.FC<ColumnFilterProps> = ({label, onChange, filterParams}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const position = useDropdownPosition(isOpen, dropdownRef, 600);

    // Get initial mode from filter params or feed type
    const getInitialMode = useCallback((): FilterMode => {
        // If there's a feed type, use it to determine mode
        const feedType = typeof filterParams.feedType === "string" ? filterParams.feedType : undefined;
        if (feedType === "intelligence_fusion") return "types";
        if (feedType === "samdesk") return "tags";

        // Otherwise use explicit filterMode
        if (filterParams.filterMode === "types") return "types";
        if (filterParams.filterMode === "tags") return "tags";

        // Default to tags if nothing selected
        if (filterParams.categoryTypeIDs || filterParams.tags) {
            return filterParams.categoryTypeIDs ? "types" : "tags";
        }
        return "tags";
    }, [filterParams]);

    // Applied state (what's currently in the filter)
    const [appliedFilterMode, setAppliedFilterMode] = useState<FilterMode>(getInitialMode);
    const [appliedTypeIDs, setAppliedTypeIDs] = useState<string[]>(
        Array.isArray(filterParams.categoryTypeIDs) ? filterParams.categoryTypeIDs : []
    );

    // Staged state (what the user is selecting)
    const [filterMode, setFilterMode] = useState<FilterMode>(getInitialMode);
    const [selectedTypeIDs, setSelectedTypeIDs] = useState<string[]>(
        Array.isArray(filterParams.categoryTypeIDs) ? filterParams.categoryTypeIDs : []
    );

    // Tags state
    const getInitialTags = useCallback((): Tag[] => {
        if (filterParams.tags && Array.isArray(filterParams.tags)) {
            const tagSlugs = filterParams.tags;
            const tagLabels = Array.isArray(filterParams.tagLabels) ? filterParams.tagLabels : [];
            return tagSlugs.map((slug, index) => [slug, tagLabels[index] || slug] as Tag);
        }
        return [];
    }, [filterParams.tags, filterParams.tagLabels]);

    const getInitialExcludedTags = useCallback((): Tag[] => {
        if (filterParams.excludedTags && Array.isArray(filterParams.excludedTags)) {
            const tagSlugs = filterParams.excludedTags;
            const tagLabels = Array.isArray(filterParams.excludedTagLabels) ? filterParams.excludedTagLabels : [];
            return tagSlugs.map((slug, index) => [slug, tagLabels[index] || slug] as Tag);
        }
        return [];
    }, [filterParams.excludedTags, filterParams.excludedTagLabels]);

    const getInitialTagMode = useCallback((): "all" | "any" => {
        if (filterParams.tagMode === "all" || filterParams.tagMode === "any") {
            return filterParams.tagMode;
        }
        return "any";
    }, [filterParams.tagMode]);

    const [appliedIncludedTags, setAppliedIncludedTags] = useState<Tag[]>(getInitialTags);
    const [appliedExcludedTags, setAppliedExcludedTags] = useState<Tag[]>(getInitialExcludedTags);
    const [appliedTagMode, setAppliedTagMode] = useState<"all" | "any">(getInitialTagMode);

    const [includedTags, setIncludedTags] = useState<Tag[]>(getInitialTags);
    const [excludedTags, setExcludedTags] = useState<Tag[]>(getInitialExcludedTags);
    const [tagMode, setTagMode] = useState<"all" | "any">(getInitialTagMode);

    const prevFeedTypeRef = useRef<string | undefined>(
        typeof filterParams.feedType === "string" ? filterParams.feedType : undefined
    );

    const handleToggle = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        if (!isOpen) {
            // When opening, sync staged state with applied state
            setFilterMode(appliedFilterMode);
            setSelectedTypeIDs(appliedTypeIDs);
            setIncludedTags(appliedIncludedTags);
            setExcludedTags(appliedExcludedTags);
            setTagMode(appliedTagMode);
        }
        setIsOpen(!isOpen);
    }, [isOpen, appliedFilterMode, appliedTypeIDs, appliedIncludedTags, appliedExcludedTags, appliedTagMode]);

    const handleModeChange = useCallback((mode: FilterMode) => {
        setFilterMode(mode);
        // Clear the other mode's selections when switching
        if (mode === "types") {
            setIncludedTags([]);
            setExcludedTags([]);
        } else {
            setSelectedTypeIDs([]);
        }
    }, []);

    const handleApply = useCallback(() => {
        // Apply the staged selections
        setAppliedFilterMode(filterMode);
        setAppliedTypeIDs(selectedTypeIDs);
        setAppliedIncludedTags(includedTags);
        setAppliedExcludedTags(excludedTags);
        setAppliedTagMode(tagMode);

        if (filterMode === "types") {
            if (selectedTypeIDs.length > 0) {
                onChange({
                    filterMode: "types",
                    categoryTypeIDs: selectedTypeIDs,
                    // Clear tags-related keys
                    tags: undefined,
                    tagLabels: undefined,
                    excludedTags: undefined,
                    excludedTagLabels: undefined,
                    tagMode: undefined,
                });
            } else {
                onChange({
                    filterMode: undefined,
                    categoryTypeIDs: undefined,
                    tags: undefined,
                    tagLabels: undefined,
                    excludedTags: undefined,
                    excludedTagLabels: undefined,
                    tagMode: undefined,
                });
            }
        } else {
            if (includedTags.length > 0 || excludedTags.length > 0) {
                onChange({
                    filterMode: "tags",
                    tags: includedTags.map(t => t[0]),
                    tagLabels: includedTags.map(t => t[1]),
                    excludedTags: excludedTags.map(t => t[0]),
                    excludedTagLabels: excludedTags.map(t => t[1]),
                    tagMode,
                    // Clear types-related keys
                    categoryTypeIDs: undefined,
                });
            } else {
                onChange({
                    filterMode: undefined,
                    categoryTypeIDs: undefined,
                    tags: undefined,
                    tagLabels: undefined,
                    excludedTags: undefined,
                    excludedTagLabels: undefined,
                    tagMode: undefined,
                });
            }
        }
        setIsOpen(false);
    }, [filterMode, selectedTypeIDs, includedTags, excludedTags, tagMode, onChange]);

    const handleClear = useCallback(() => {
        setSelectedTypeIDs([]);
        setIncludedTags([]);
        setExcludedTags([]);
        setAppliedTypeIDs([]);
        setAppliedIncludedTags([]);
        setAppliedExcludedTags([]);
        onChange({
            filterMode: undefined,
            categoryTypeIDs: undefined,
            tags: undefined,
            tagLabels: undefined,
            excludedTags: undefined,
            excludedTagLabels: undefined,
            tagMode: undefined,
        });
    }, [onChange]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    // Clear selections when feed type changes
    useEffect(() => {
        const currentFeedType = typeof filterParams.feedType === "string" ? filterParams.feedType : undefined;
        const prevFeedType = prevFeedTypeRef.current;

        if (prevFeedType !== undefined && currentFeedType !== prevFeedType) {
            // Feed type changed, clear all selections
            setSelectedTypeIDs([]);
            setIncludedTags([]);
            setExcludedTags([]);
            setAppliedTypeIDs([]);
            setAppliedIncludedTags([]);
            setAppliedExcludedTags([]);
            onChange({
                filterMode: undefined,
                categoryTypeIDs: undefined,
                tags: undefined,
                tagLabels: undefined,
                excludedTags: undefined,
                excludedTagLabels: undefined,
                tagMode: undefined,
            });
        }

        prevFeedTypeRef.current = currentFeedType;
    }, [filterParams.feedType, onChange]);

    // Update state when filterParams change (e.g., from URL)
    useEffect(() => {
        const mode = getInitialMode();
        setAppliedFilterMode(mode);
        setFilterMode(mode);

        if (mode === "types" && Array.isArray(filterParams.categoryTypeIDs)) {
            setAppliedTypeIDs(filterParams.categoryTypeIDs);
            setSelectedTypeIDs(filterParams.categoryTypeIDs);
        } else if (mode === "tags") {
            const tags = getInitialTags();
            const excludedTagsList = getInitialExcludedTags();
            const tagModeValue = getInitialTagMode();
            setAppliedIncludedTags(tags);
            setAppliedExcludedTags(excludedTagsList);
            setAppliedTagMode(tagModeValue);
            setIncludedTags(tags);
            setExcludedTags(excludedTagsList);
            setTagMode(tagModeValue);
        }
    }, [getInitialMode, getInitialTags, getInitialExcludedTags, getInitialTagMode, filterParams]);

    const getFilterCount = useCallback(() => {
        if (appliedFilterMode === "types") {
            return appliedTypeIDs.length;
        } else {
            return appliedIncludedTags.length + appliedExcludedTags.length;
        }
    }, [appliedFilterMode, appliedTypeIDs.length, appliedIncludedTags.length, appliedExcludedTags.length]);

    const filterCount = getFilterCount();
    const feedType = typeof filterParams.feedType === "string" ? filterParams.feedType : undefined;
    const hasFeedType = !!feedType;

    // Determine label based on feed type
    const displayLabel = hasFeedType
        ? (feedType === "intelligence_fusion" ? "Types" : "Tags")
        : label;

    return (
        <div className="relative inline-block" ref={dropdownRef}>
            <button
                onClick={handleToggle}
                className="flex items-center gap-1 hover:text-blue-600 transition-colors font-bold"
            >
                <span>{displayLabel}</span>
                {filterCount > 0 && (
                    <span className="text-xs text-blue-600 font-normal">
                        {appliedFilterMode === "types"
                            ? `(${appliedTypeIDs.length} types)`
                            : `(${appliedIncludedTags.length}${appliedExcludedTags.length > 0 ? ` +${appliedExcludedTags.length} excl` : ""} tags)`
                        }
                    </span>
                )}
                <span className={`text-xs transition-transform ${isOpen ? "rotate-180" : ""}`}>▼</span>
            </button>

            {isOpen && (
                <div className={`absolute top-full ${position === "left" ? "left-0" : "right-0"} mt-1 bg-white rounded shadow-lg z-50 w-[600px] flex flex-col max-h-[80vh]`} onClick={(e) => e.stopPropagation()}>
                    {/* Mode Toggle - only show if no feed type is set */}
                    {!hasFeedType && (
                        <div className="p-3">
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handleModeChange("tags")}
                                    className={`flex-1 px-4 py-2 rounded font-medium transition-colors ${
                                        filterMode === "tags"
                                            ? "bg-blue text-white"
                                            : "bg-light-light-grey text-grey hover:bg-light-grey"
                                    }`}
                                >
                                    Filter by Tags
                                </button>
                                <button
                                    onClick={() => handleModeChange("types")}
                                    className={`flex-1 px-4 py-2 rounded font-medium transition-colors ${
                                        filterMode === "types"
                                            ? "bg-blue text-white"
                                            : "bg-light-light-grey text-grey hover:bg-light-grey"
                                    }`}
                                >
                                    Filter by Types
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Header when feed type is set */}
                    {hasFeedType && (
                        <div className="p-3 border-b">
                            <div className="font-medium text-grey">
                                Filter by {filterMode === "types" ? "Types" : "Tags"}
                            </div>
                        </div>
                    )}

                    {/* Content Area */}
                    <div className="flex-1 overflow-auto p-3">
                        {filterMode === "types" ? (
                            <CategorySelector
                                selectedTypeIDs={selectedTypeIDs}
                                onSelectedTypeIDsChange={setSelectedTypeIDs}
                            />
                        ) : (
                            <TagSelector
                                includedTags={includedTags}
                                excludedTags={excludedTags}
                                tagMode={tagMode}
                                onIncludedTagsChange={setIncludedTags}
                                onExcludedTagsChange={setExcludedTags}
                                onTagModeChange={setTagMode}
                            />
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="p-3 flex gap-2">
                        <ButtonV2 label="Clear" color="gray" onClick={handleClear} />
                        <ButtonV2 label="Apply Filter" color="blue" onClick={handleApply} />
                    </div>
                </div>
            )}
        </div>
    );
};
